import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const COOKIE_KEY = 'prebedai_cookie_consent';

type ConsentState = 'pending' | 'accepted' | 'rejected' | 'custom';

interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
}

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const navigate = useNavigate();
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const stored = localStorage.getItem(COOKIE_KEY);
    if (!stored) {
      // Small delay so the page loads first
      const timer = setTimeout(() => setVisible(true), 1200);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    const consent = { state: 'accepted' as ConsentState, preferences: { necessary: true, analytics: true, marketing: true }, date: new Date().toISOString() };
    localStorage.setItem(COOKIE_KEY, JSON.stringify(consent));
    setVisible(false);
    // Update Google Analytics Consent Mode dynamically
    if (typeof window.gtag === 'function') {
      window.gtag('consent', 'update', {
        'analytics_storage': 'granted'
      });
    }
  };

  const handleRejectAll = () => {
    const consent = { state: 'rejected' as ConsentState, preferences: { necessary: true, analytics: false, marketing: false }, date: new Date().toISOString() };
    localStorage.setItem(COOKIE_KEY, JSON.stringify(consent));
    setVisible(false);
    // Update Google Analytics Consent Mode to denied
    if (typeof window.gtag === 'function') {
      window.gtag('consent', 'update', {
        'analytics_storage': 'denied'
      });
    }
  };

  const handleSavePreferences = () => {
    const consent = { state: 'custom' as ConsentState, preferences: { ...preferences, necessary: true }, date: new Date().toISOString() };
    localStorage.setItem(COOKIE_KEY, JSON.stringify(consent));
    setVisible(false);
    // Update Google Analytics Consent Mode based on user preference
    if (typeof window.gtag === 'function') {
      window.gtag('consent', 'update', {
        'analytics_storage': preferences.analytics ? 'granted' : 'denied'
      });
    }
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[9999] p-3 sm:p-4 animate-fade-in">
      <div className="max-w-3xl mx-auto bg-[#0A1628] border border-white/15 rounded-2xl shadow-2xl overflow-hidden">
        {/* Main banner */}
        <div className="p-4 sm:p-6">
          <div className="flex items-start gap-3 mb-3">
            <div className="w-9 h-9 sm:w-10 sm:h-10 bg-[#06B6D4]/15 rounded-xl flex items-center justify-center flex-shrink-0">
              <i className="ri-shield-check-line text-[#06B6D4] text-lg sm:text-xl"></i>
            </div>
            <div className="flex-1">
              <h3 className="text-white font-bold text-sm sm:text-base mb-1">Uso de cookies</h3>
              <p className="text-white/70 text-xs sm:text-sm leading-relaxed">
                Utilizamos cookies propias y de terceros para mejorar tu experiencia, analizar el tráfico y personalizar contenido. 
                Puedes aceptar todas, rechazarlas o configurar tus preferencias.{' '}
                <button 
                  onClick={() => { setVisible(false); navigate('/politica-cookies'); }}
                  className="text-[#06B6D4] hover:underline font-medium"
                >
                  Más información
                </button>
              </p>
            </div>
          </div>

          {/* Configuration panel */}
          {showConfig && (
            <div className="mt-4 pt-4 border-t border-white/10 space-y-3 animate-fade-in">
              {/* Necessary */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white text-xs sm:text-sm font-medium">Necesarias</p>
                  <p className="text-white/50 text-[10px] sm:text-xs">Imprescindibles para el funcionamiento</p>
                </div>
                <div className="w-10 h-5 bg-[#06B6D4] rounded-full flex items-center px-0.5 opacity-60 cursor-not-allowed">
                  <div className="w-4 h-4 bg-white rounded-full ml-auto shadow-sm"></div>
                </div>
              </div>

              {/* Analytics */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white text-xs sm:text-sm font-medium">Analíticas</p>
                  <p className="text-white/50 text-[10px] sm:text-xs">Nos ayudan a mejorar la web</p>
                </div>
                <button 
                  onClick={() => setPreferences(p => ({ ...p, analytics: !p.analytics }))}
                  className={`w-10 h-5 rounded-full flex items-center px-0.5 transition-colors duration-200 ${
                    preferences.analytics ? 'bg-[#06B6D4]' : 'bg-white/20'
                  }`}
                >
                  <div className={`w-4 h-4 bg-white rounded-full shadow-sm transition-transform duration-200 ${
                    preferences.analytics ? 'translate-x-5' : 'translate-x-0'
                  }`}></div>
                </button>
              </div>

              {/* Marketing */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white text-xs sm:text-sm font-medium">Marketing</p>
                  <p className="text-white/50 text-[10px] sm:text-xs">Contenido personalizado</p>
                </div>
                <button 
                  onClick={() => setPreferences(p => ({ ...p, marketing: !p.marketing }))}
                  className={`w-10 h-5 rounded-full flex items-center px-0.5 transition-colors duration-200 ${
                    preferences.marketing ? 'bg-[#06B6D4]' : 'bg-white/20'
                  }`}
                >
                  <div className={`w-4 h-4 bg-white rounded-full shadow-sm transition-transform duration-200 ${
                    preferences.marketing ? 'translate-x-5' : 'translate-x-0'
                  }`}></div>
                </button>
              </div>
            </div>
          )}

          {/* Buttons */}
          <div className="flex flex-col sm:flex-row gap-2 mt-4">
            {showConfig ? (
              <button
                onClick={handleSavePreferences}
                className="flex-1 px-4 py-2.5 bg-gradient-to-r from-[#06B6D4] to-[#0891B2] text-white rounded-xl text-xs sm:text-sm font-semibold hover:shadow-lg hover:shadow-[#06B6D4]/30 transition-all"
              >
                Guardar preferencias
              </button>
            ) : (
              <>
                <button
                  onClick={handleAcceptAll}
                  className="flex-1 px-4 py-2.5 bg-gradient-to-r from-[#06B6D4] to-[#0891B2] text-white rounded-xl text-xs sm:text-sm font-semibold hover:shadow-lg hover:shadow-[#06B6D4]/30 transition-all"
                >
                  Aceptar todas
                </button>
                <button
                  onClick={handleRejectAll}
                  className="flex-1 px-4 py-2.5 border border-white/20 text-white/80 rounded-xl text-xs sm:text-sm font-medium hover:bg-white/5 transition-all"
                >
                  Rechazar
                </button>
              </>
            )}
            <button
              onClick={() => setShowConfig(!showConfig)}
              className="flex-1 px-4 py-2.5 border border-white/20 text-white/80 rounded-xl text-xs sm:text-sm font-medium hover:bg-white/5 transition-all flex items-center justify-center gap-1.5"
            >
              <i className={`ri-settings-3-line text-sm ${showConfig ? 'text-[#06B6D4]' : ''}`}></i>
              <span>{showConfig ? 'Ocultar opciones' : 'Configurar'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
